/**
 * The approval gates G-1..G-9 (IP-62; design spec 8.6; SPEC 16.11.1, codes
 * and messages from SPEC 16.5.2; docs/orders 4.6), evaluated on ONE approval
 * unit - one order, or both legs of a switch or transfer - exactly as the
 * unit's own transaction read it under FOR UPDATE SKIP LOCKED (sql.js
 * lockUnit). Pure: every fact arrives as an argument, so each code is tested
 * alone (gates.test.js).
 *
 *   G-1  still READY, and at the state_version the Console rendered   APR-001, APR-012
 *   G-4  a pair only whole: both legs in the request, both READY      APR-011
 *   G-2  a subscription's money MATCHED, CLEARED, its match ACTIVE     APR-004, APR-005
 *        - and (IP-193) a redemption's or switch-out's payout fully
 *        reconciled with the bank statement (CONFIRMED)               APR-015
 *   G-3  the price published, not superseded, struck after receipt    APR-002, APR-003
 *        (a transfer leg: its dealing date's price published, IP-192)
 *        - and a subscription's amount buys at least one 0.0001 unit
 *        at the price it deals at (IP-203: with fees applied, the
 *        selling price with entry fee)                                APR-014
 *   G-5  the approver ACTIVE and holding core.order.approve in the
 *        Core's own rows (re-read in the unit's transaction)          APR-007
 *   G-6  four eyes on every leg: reviewed in its current cycle, and
 *        the approver none of its reviewers, of ANY cycle (IP-128).
 *        (Its LIV half - the approver is not LIV's approver - went
 *        with LIV's approval, IP-83: the Core's is the only one.)    APR-006
 *   G-7  an OUT leg's units within the holder's balance                APR-008
 *   G-8  the fund and the holder ACTIVE, the book CLOSED               APR-009, APR-010, APR-013
 *   G-9  two authorised legs by two people: the review's ORDER_REVIEW
 *        (L1) evidence, the reviewer's own, and this approval's - a
 *        code or the MFA session alike (IP-76)                        ASSERTION_REQUIRED,
 *                                                                     ASSERTION_INVALID, MAKER_IS_CHECKER
 *
 * The checker's send-back (ORDER_SEND_BACK, IP-85) takes the review off -
 * reviewer and evidence - and moves state_version on, so a unit sent back is
 * refused by G-1 (the version a stale screen rendered) or G-6 (no maker)
 * until it is reviewed again. Nobody sets a fee % per transaction (IP-203):
 * the review only waives the fund's fee, and the fee is in the price.
 *
 * G-4 is checked straight after G-1 because both read the unit's shape; the
 * rest run in number order. The first failure is the answer, and it skips the
 * whole unit (both legs of a pair), leaving it READY.
 *
 * The register writer checks its own G1..G7 again inside the same
 * transaction (register/post.js); a shortfall of lots there is LFOC-ALLOC-004,
 * which approve.js turns into a rejection.
 */
import { div, gt, isDecimal, isPositive, toFixed } from '../decimal.js';
import { feeTermsOf } from './fees.js';

/** SPEC 16.5.2's approval codes. */
export const APR = Object.freeze({
  NOT_READY: 'APR-001',
  NO_PRICE: 'APR-002',
  PRICE_CORRECTED: 'APR-003',
  NOT_FUNDED: 'APR-004',
  FUNDS_RETURNED: 'APR-005',
  FOUR_EYES: 'APR-006',
  NOT_CHECKER: 'APR-007',
  HOLDING: 'APR-008',
  FUND_SUSPENDED: 'APR-009',
  HOLDER_INACTIVE: 'APR-010',
  PAIR: 'APR-011',
  CHANGED: 'APR-012',
  BOOK_OPEN: 'APR-013',
  FEE_BUYS_NO_UNITS: 'APR-014',
  // IP-193: a redemption or switch-out is paid by the bank and reconciled before it is approved.
  PAYOUT_NOT_RECONCILED: 'APR-015',
});

/** IP-193: the order types the bank pays before approval - a redemption, and a switch-out's net. */
const PAID_BEFORE_APPROVAL = new Set(['RED', 'SWI_OUT']);

/** G-9's codes (SPEC 16.11.1; D-1: an authenticator code stands in for the hardware assertion). */
export const ASSERTION = Object.freeze({
  REQUIRED: 'ASSERTION_REQUIRED',
  INVALID: 'ASSERTION_INVALID',
  MAKER_IS_CHECKER: 'MAKER_IS_CHECKER',
});

/**
 * IP-127 (design 2.1): the states an order may be reviewed in - from LIV's
 * completion (a dealing date) to approval. RECEIVED and AWAITING_LIV have no
 * dealing date yet; a HELD order is released first; APPROVED and later, and
 * the final states, are past review. An order frozen before IP-125 (with no
 * review) is reviewed after the freeze: approval needs it (G-6). Here, beside
 * the approval codes, so the Console can read it with the rest of the gates.
 */
export const REVIEWABLE_STATES = Object.freeze(['VALIDATED', 'AWAITING_FUNDS', 'AWAITING_CLEARANCE', 'MATCHED_CLEARED', 'FROZEN', 'PRICED', 'READY']);

/** IP-130 (design 3): the open states the checker's ORDER_REJECT takes an order from - every state before APPROVED, HELD included. */
export const REJECTABLE_STATES = Object.freeze(['RECEIVED', 'AWAITING_LIV', 'VALIDATED', 'AWAITING_FUNDS', 'AWAITING_CLEARANCE', 'MATCHED_CLEARED',
  'FROZEN', 'PRICED', 'READY', 'HELD']);

/** The claim G-5 re-reads (spec 6.1: ORDER_APPROVE). */
export const APPROVE_CLAIM = 'core.order.approve';

const OUT_LEGS = new Set(['RED', 'SWI_OUT', 'TRF_OUT']);
const PAIR_LEGS = new Set(['SWI_OUT', 'SWI_IN', 'TRF_OUT', 'TRF_IN']);
const TRANSFER_LEGS = new Set(['TRF_OUT', 'TRF_IN']);
const MONEY_IN = new Set(['MATCHED', 'CONFIRMED']);

const same = (a, b) => typeof a === 'string' && typeof b === 'string' && a !== '' && a.toLowerCase() === b.toLowerCase();
const ref = (o) => o.transaction_ref ?? o.order_id;

function fail(gate, code, order, message) {
  return { gate, code, orderId: order.order_id, message };
}

/** G-1 and G-4: the unit's shape - READY, unchanged, and a pair only whole. */
function readyAndWhole(legs, expectedVersions) {
  const notReady = legs.filter((o) => o.state !== 'READY');
  if (notReady.length === legs.length) {
    const o = notReady[0];
    return fail('G-1', APR.NOT_READY, o, 'Transaction ' + ref(o) + ' is ' + o.state + '; nothing to approve.');
  }
  if (notReady.length) {
    const o = notReady[0];
    return fail('G-4', APR.PAIR, o, 'Linked leg ' + ref(o) + ' is ' + o.state + '.');
  }
  for (const o of legs) {
    const expected = expectedVersions?.[o.order_id];
    if (!Number.isSafeInteger(expected) || expected !== Number(o.state_version)) {
      return fail('G-1', APR.CHANGED, o, 'Transaction changed since the queue was loaded; refresh. (' + ref(o) + ' is at version '
        + Number(o.state_version) + ', not ' + (expected ?? 'none') + '.)');
    }
  }
  const ids = new Set(legs.map((o) => o.order_id));
  for (const o of legs) {
    if (!PAIR_LEGS.has(o.order_type)) continue;
    if (!o.linked_order_id) {
      return fail('G-4', APR.PAIR, o, 'Transaction ' + ref(o) + ' is one leg of a pair but is linked to no other leg.');
    }
    if (!ids.has(o.linked_order_id)) {
      const partner = o.partner_ref ?? o.linked_order_id;
      return fail('G-4', APR.PAIR, o, 'Linked leg ' + partner + ' is ' + (o.partner_state ?? 'unknown')
        + ' and not in this request; the two legs of a pair are approved together.');
    }
  }
  return null;
}

/**
 * G-2's other half (IP-193, owner 17 Oct 2026): "before approve the transaction need to
 * match with statement". A redemption or switch-out with a net to pay is approved only once
 * the bank statement shows it paid in full - its payout (raised when it was priced and
 * accepted, cash/payout-expect.js) CONFIRMED by its debits (cash/payout-match.js). A
 * transfer moves units, not money, and needs none; nor does a switch-in.
 */
function paidOut(o) {
  if (!PAID_BEFORE_APPROVAL.has(o.order_type)) return null;
  // A net of 0.00 pays nothing, so there is nothing to reconcile.
  if (o.net_amount !== null && o.net_amount !== undefined && isDecimal(String(o.net_amount)) && !isPositive(o.net_amount)) return null;
  if (o.payout_state === 'CONFIRMED') return null;
  const paid = o.payout_state ? toFixed(o.payout_matched ?? '0', 2) + ' of ' + toFixed(o.payout_amount, 2) : 'nothing';
  return fail('G-2', APR.PAYOUT_NOT_RECONCILED, o, 'Payout not reconciled: the bank statement has not shown transaction ' + ref(o)
    + ' paid (' + paid + ' LKR reconciled). Reconcile it before approving.');
}

/** G-2: a subscription's money is matched and cleared (Rule 35); a redemption's or switch-out's payout reconciled (IP-193). */
function funded(o) {
  if (o.order_type !== 'SUB') return paidOut(o);
  if (!o.cash_match_id || o.match_state !== 'ACTIVE' || !MONEY_IN.has(o.movement_state)) {
    return fail('G-2', APR.NOT_FUNDED, o, 'Cleared funds not evidenced (Rule 35): transaction ' + ref(o) + ' has no active match to money received.');
  }
  if (o.clearance_state !== 'CLEARED') {
    return fail('G-2', APR.FUNDS_RETURNED, o, o.clearance_state === 'RETURNED'
      ? 'Funds returned; transaction back to Awaiting funds.'
      : 'Funds for transaction ' + ref(o) + ' are ' + (o.clearance_state ?? 'not received') + ', not cleared (Rule 35).');
  }
  return null;
}

/** G-3: the dealing date's price, published and current, and struck after the order was received (forward pricing). */
function priced(o, prices, pricedWith) {
  // IP-192 (owner, 17 Oct 2026): no transaction is approved before transaction pricing for its
  // dealing date - a transfer included. A transfer moves units at cost, so it is never priced
  // itself: it only needs its dealing date's price to be published.
  if (TRANSFER_LEGS.has(o.order_type)) {
    return prices?.get(o.fund_id + '|' + o.dealing_date)
      ? null
      : fail('G-3', APR.NO_PRICE, o, 'No published price for ' + o.fund_id + ' on ' + o.dealing_date + ': a transfer is approved after transaction pricing.');
  }
  const own = o.price_id ? pricedWith?.get(o.price_id) ?? null : null;
  const where = o.fund_id + ' ' + o.dealing_date;
  if (own?.superseded_at) {
    return fail('G-3', APR.PRICE_CORRECTED, o, 'Price for ' + where + ' was corrected at ' + own.superseded_at + '; the transaction waits to be priced again.');
  }
  const current = prices?.get(o.fund_id + '|' + o.dealing_date) ?? null;
  if (!current) return fail('G-3', APR.NO_PRICE, o, 'No published price for ' + o.fund_id + ' on ' + o.dealing_date + '.');
  if (o.price_id && current.price_id !== o.price_id) {
    return fail('G-3', APR.PRICE_CORRECTED, o, 'Price for ' + where + ' was corrected at ' + current.published_at + '; the transaction waits to be priced again.');
  }
  if (o.received_at && !(Date.parse(current.published_at) > Date.parse(o.received_at))) {
    return fail('G-3', APR.NO_PRICE, o, 'No published price for ' + o.fund_id + ' on ' + o.dealing_date
      + ' struck after the transaction was received (forward pricing).');
  }
  return null;
}

/**
 * G-3's other half: a subscription's amount must buy at least one 0.0001
 * unit at the price it deals at - the register's own arithmetic
 * (register/allocate.js allocateSub). IP-203: that price is the one
 * feeTermsOf chooses, as repricing and allocation do - the selling price with
 * entry fee when the fund charges a front-end fee and the review did not
 * waive it, else the selling price. A tiny subscription at a high price
 * (LKR 0.01 at 300.0000) would otherwise fail every approval in the register
 * (LFOC-ALLOC-007), counted towards the three-failure stop. Refused here
 * instead, the unit is SKIPPED, and the checker rejects it.
 */
function feeLeavesUnits(o, prices) {
  if (o.order_type !== 'SUB' || !isDecimal(o.amount)) return null;
  const price = prices?.get(o.fund_id + '|' + o.dealing_date) ?? null;
  const selling = price?.selling_price;
  if (!isDecimal(selling) || !isPositive(selling)) return null; // the register refuses a price without one
  // IP-204: no config, so a leg nobody has chosen for (rare after review: the review keeps its box) reads
  // as ticked - the price with entry fee buys the FEWEST units, so G-3 is never looser than allocation.
  const terms = feeTermsOf({ order: o, price });
  const dealing = terms.applied ? String(price.selling_price_with_entry_fee) : String(selling);
  const amount = toFixed(o.amount, 2);
  if (isPositive(div(amount, dealing, 4))) return null;
  return fail('G-3', APR.FEE_BUYS_NO_UNITS, o, 'LKR ' + amount + ' on transaction ' + ref(o) + ' buys no unit at '
    + toFixed(dealing, 4) + '; reject it.');
}

/**
 * G-6: four eyes (IP-128, design 2.8) - the order's current review cycle has a
 * review (review_count > 0; a send-back ends a cycle), and the approver is
 * NONE of its reviewers: o.reviewers, every REVIEWED row's actor from every
 * cycle (sql.js selectReviewers, read after the unit's lock), and the latest
 * reviewer on the row. Since IP-83 LIV no longer approves, so there is no LIV
 * approver to compare with: the order's liv_approver_ref is always NULL (kept
 * only because 0005 is applied), and the approver's liv_subject admits nobody
 * since IP-72. The maker and the checker are both the Core's people.
 */
function fourEyes(o, approver) {
  const count = o.review_count === undefined || o.review_count === null ? null : Number(o.review_count);
  if (typeof o.reviewed_by !== 'string' || !o.reviewed_by || count === 0) {
    return fail('G-6', APR.FOUR_EYES, o, 'Transaction ' + ref(o) + ' has not been reviewed.');
  }
  if ([o.reviewed_by, ...(o.reviewers ?? [])].some((uid) => same(uid, approver.uid))) {
    return fail('G-6', APR.FOUR_EYES, o, 'You reviewed transaction ' + ref(o) + '; someone who has not reviewed it must approve it.');
  }
  return null;
}

/** G-7: an OUT leg's units within the holder's balance. A full holding or an AMOUNT leg is sized by the register. */
function withinHolding(o) {
  if (!OUT_LEGS.has(o.order_type) || o.is_full_holding === true || o.units === null || o.units === undefined) return null;
  if (o.order_type !== 'TRF_OUT' && o.basis !== 'UNITS') return null;
  const held = toFixed(o.balance_units ?? '0', 4);
  if (gt(o.units, held)) {
    return fail('G-7', APR.HOLDING, o, 'Holding is ' + held + '; transaction ' + ref(o) + ' redeems ' + toFixed(o.units, 4) + '.');
  }
  return null;
}

/** G-8: the fund and the holder are ACTIVE, and the book is CLOSED. */
function open(o) {
  if (o.fund_status !== 'ACTIVE') {
    return fail('G-8', APR.FUND_SUSPENDED, o, o.fund_status === 'SUSPENDED' ? 'Fund suspended (Rule 55).' : 'Fund ' + o.fund_id + ' is ' + o.fund_status + '.');
  }
  if (o.holder_status !== 'ACTIVE') {
    return fail('G-8', APR.HOLDER_INACTIVE, o, 'Holder ' + (o.holder_no ?? o.holder_id) + ' is ' + o.holder_status + '.');
  }
  if (o.book_state !== 'CLOSED') {
    return fail('G-8', APR.BOOK_OPEN, o, 'Book ' + o.fund_id + ' ' + o.dealing_date + ' is still open.');
  }
  return null;
}

/** G-9: the review's step-up and this approval's, typed by two people. */
function twoStepUps(o, approver, evidence) {
  if (!evidence || typeof evidence.evidenceId !== 'string' || !evidence.evidenceId) {
    return fail('G-9', ASSERTION.REQUIRED, o, 'This approval carries no step-up evidence.');
  }
  if (!o.review_evidence_id) {
    return fail('G-9', ASSERTION.REQUIRED, o, 'The review of transaction ' + ref(o) + ' carries no step-up evidence; review it again.');
  }
  if (o.review_evidence_action !== 'ORDER_REVIEW' || o.review_evidence_class !== 'L1' || !same(o.review_evidence_uid, o.reviewed_by)) {
    return fail('G-9', ASSERTION.INVALID, o, 'The review evidence of transaction ' + ref(o) + ' is not its reviewer\'s ORDER_REVIEW step-up.');
  }
  if (same(o.review_evidence_uid, approver.uid) || same(o.review_evidence_uid, evidence.uid)) {
    return fail('G-9', ASSERTION.MAKER_IS_CHECKER, o, 'The review and the approval of transaction ' + ref(o) + ' were confirmed by the same person.');
  }
  return null;
}

/**
 * The first gate the unit fails, or null when it may be posted.
 *
 * @param {object} facts
 * @param {object[]} facts.legs the unit's orders as sql.js lockUnit returns them (posting order), each with
 *   reviewers: every reviewer's uid of every cycle (sql.js selectReviewers; G-6)
 * @param {Record<string, number>} facts.expectedVersions orderId -> the state_version the Console rendered
 * @param {{ uid: string, status: string, holdsClaim: boolean }|null} facts.approver
 *   the approver's console_user row, read in the unit's transaction
 * @param {{ evidenceId: string, uid: string }|null} facts.evidence this approval's ORDER_APPROVE evidence
 * @param {Map<string, object>} facts.prices 'fundId|dealingDate' -> the current (non-superseded) price row, if any
 * @param {Map<string, object>} facts.pricedWith price_id -> the row each order was priced with
 * @returns {{ gate: string, code: string, orderId: string, message: string }|null}
 */
export function evaluateGates({ legs, expectedVersions, approver, evidence, prices, pricedWith }) {
  if (!Array.isArray(legs) || !legs.length) throw new TypeError('evaluateGates needs the unit\'s orders');
  const shape = readyAndWhole(legs, expectedVersions);
  if (shape) return shape;
  for (const o of legs) {
    const failure = funded(o) ?? priced(o, prices, pricedWith) ?? feeLeavesUnits(o, prices);
    if (failure) return failure;
  }
  if (!approver || approver.status !== 'ACTIVE' || approver.holdsClaim !== true) {
    return fail('G-5', APR.NOT_CHECKER, legs[0], 'Approval requires the Ops Checker role (' + APPROVE_CLAIM + ') in the Core\'s own records.');
  }
  for (const check of [(o) => fourEyes(o, approver), withinHolding, open, (o) => twoStepUps(o, approver, evidence)]) {
    for (const o of legs) {
      const failure = check(o);
      if (failure) return failure;
    }
  }
  return null;
}
